import type { Dispatch, SetStateAction } from "react";

import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { z } from "zod";

export function jsonCodec<T extends z.core.$ZodType>(schema: T) {
  return z.codec(z.string(), schema, {
    decode: (jsonString, ctx) => {
      try {
        return JSON.parse(jsonString) as z.input<T>;
      } catch (error: unknown) {
        ctx.issues.push({
          code: "invalid_format",
          format: "json",
          input: jsonString,
          message: error instanceof Error ? error.message : String(error),
        });

        return z.NEVER;
      }
    },
    encode: (value) => JSON.stringify(value),
  });
}

function dispatchStorageEvent(key: string, newValue: string | null) {
  window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}

export function getLocalStorageItem(key: string) {
  return window.localStorage.getItem(key);
}

export function setLocalStorageItem(key: string, stringifiedValue: string) {
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
}

export function removeLocalStorageItem(key: string) {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
}

function subscribe(callback: () => void) {
  window.addEventListener("storage", callback);

  return () => {
    window.removeEventListener("storage", callback);
  };
}

function getServerSnapshot(): string | null {
  throw new Error("useLocalStorage is a client-side only hook");
}

function useLocalStorageHelper<Value>(
  key: string,
  initialValue: Value,
  schema: NoInfer<z.core.$ZodType<Value>>,
): [Value, Dispatch<SetStateAction<Value>>] {
  const getSnapshot = useCallback(() => getLocalStorageItem(key), [key]);

  const store = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const codec = useMemo(() => jsonCodec(schema), [schema]);

  const value = useMemo(() => {
    if (store === null) {
      return initialValue;
    }

    const decoded = codec.safeDecode(store);

    if (!decoded.success) {
      console.error(
        `Failed to decode localStorage item "${key}":`,
        z.prettifyError(decoded.error),
      );

      setLocalStorageItem(key, codec.encode(initialValue));
      return initialValue;
    }

    return decoded.data;
  }, [key, initialValue, store, codec]);

  const setState = useCallback(
    (valueFn: SetStateAction<Value>) => {
      const nextValue =
        typeof valueFn === "function"
          ? (valueFn as (prevState: Value) => Value)(value)
          : valueFn;

      if (nextValue === null || nextValue === undefined) {
        removeLocalStorageItem(key);
      } else {
        setLocalStorageItem(key, codec.encode(nextValue));
      }
    },
    [key, codec, value],
  );

  useEffect(() => {
    const item = getLocalStorageItem(key);

    if (item === null && initialValue !== null && initialValue !== undefined) {
      setLocalStorageItem(key, codec.encode(initialValue));
    }
  }, [key, codec, initialValue]);

  return [value, setState] as const;
}

export function useLocalStorage(
  key: string,
  initialValue: string,
): [string, Dispatch<SetStateAction<string>>];
export function useLocalStorage(
  key: string,
): [string | undefined, Dispatch<SetStateAction<string | undefined>>];
export function useLocalStorage<Value>(
  key: string,
  initialValue: Value,
  schema: NoInfer<z.core.$ZodType<Value>>,
): [Value, Dispatch<SetStateAction<Value>>];
export function useLocalStorage<Value>(
  key: string,
  schema: z.core.$ZodType<Value>,
): [Value | undefined, Dispatch<SetStateAction<Value | undefined>>];
export function useLocalStorage<Value>(
  key: string,
  initialValueOrSchema?: Value | string | z.core.$ZodType<Value>,
  schemaOrUndefined?: NoInfer<z.core.$ZodType<Value>>,
) {
  const isSchemaOnly = initialValueOrSchema instanceof z.core.$ZodType;

  const schema = isSchemaOnly
    ? initialValueOrSchema
    : (schemaOrUndefined ?? (z.string() as unknown as z.core.$ZodType<Value>));

  const initialValue = isSchemaOnly
    ? undefined
    : (initialValueOrSchema as Value);

  return useLocalStorageHelper(key, initialValue, schema);
}
